import React, { useEffect } from 'react';
import { colors } from '../styles/variables';
import CourseHeader from '../components/course/CourseHeader';
import CourseAbout from '../components/course/CourseAbout';
import CourseCurriculum from '../components/course/CourseCurriculum';
import CourseProjects from '../components/course/CourseProjects';
import CourseInstructors from '../components/course/CourseInstructors';
import CourseCta from '../components/course/CourseCta';
import CourseReviewSection from '../components/course/CourseReviewSection';
import Seo from '../components/common/Seo';
import { projectsByCourse } from '../data/projects';

const courseJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: 'Java 심화 / Spring',
  description: '고급 Java, Spring, 클라우드, 마이크로서비스까지 실무 중심 1:1 심화 멘토링 과정.',
  provider: {
    '@type': 'EducationalOrganization',
    name: 'ElectroWave Club',
    sameAs: 'https://electrowave.kr',
  },
  inLanguage: 'ko',
  educationalLevel: 'advanced',
  url: 'https://electrowave.kr/java-advanced',
};

const JavaAdvancedPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  
  // 코스 헤더 데이터
  const headerData = {
    tag: '고급 백엔드 개발',
    title: 'Java 심화 과정',
    description: 'Java 기본 과정을 마친 분들을 위해 고급 Java 프로그래밍 기법과 엔터프라이즈 개발 환경에서의 Java 활용법을 배웁니다. Spring 프레임워크, 클라우드 개발, 마이크로서비스 아키텍처까지 실무에서 바로 적용 가능한 고급 기술을 학습합니다.',
    duration: '총 600시간 / 24주 과정',
    level: '중급자 대상',
    bgColor: colors.javaAdvancedColor,
    bgImage: '/assets/images/course-thumb-java-advanced.png'
  };
  
  // 코스 소개 데이터
  const aboutData = {
    subtitle: 'Java 언어의 고급 기능과 엔터프라이즈 애플리케이션 개발에 필요한 프레임워크 및 기술을 배우는 심화 과정입니다.',
    forWhom: [
      'Java 기초 과정을 수료했거나 그에 준하는 지식을 갖춘 분',
      '백엔드 개발자로 경력을 쌓고 싶은 분',
      '엔터프라이즈급 애플리케이션 개발 방법을 배우고 싶은 분',
      'Spring 프레임워크를 심도 있게 학습하고 싶은 분',
      '클라우드 환경에서의 Java 애플리케이션 개발에 관심 있는 분',
      '마이크로서비스 아키텍처를 실무에 적용하고 싶은 분'
    ],
    skills: [
      '고급 Java 프로그래밍 기법 활용 능력',
      'Spring Framework와 Spring Boot를 이용한 애플리케이션 개발 역량',
      '데이터베이스 설계 및 최적화 능력',
      'RESTful API 설계 및 개발 기술',
      '마이크로서비스 아키텍처 설계 및 구현 능력',
      '클라우드 환경에서의 배포 및 운영 관리 역량',
      '대용량 데이터 처리 및 성능 최적화 기술'
    ],
    imageSrc: '/assets/images/course-thumb-java-advanced.png'
  };
  
  // 커리큘럼 데이터
  const curriculumData = {
    subtitle: '실무 중심의 프로젝트를 통해 고급 Java 개발 역량을 체계적으로 키웁니다.',
    modules: [
      {
        title: '1. 고급 Java 프로그래밍',
        description: 'Java의 고급 기능과 디자인 패턴을 학습하여 효율적이고 유지보수가 용이한 코드를 작성하는 방법을 배웁니다.',
        lessons: [
          'Java 8+ 기능 활용 (Lambda, Stream API, Optional)',
          '컬렉션 프레임워크 심화 학습',
          '제네릭스 고급 활용',
          '멀티스레딩과 동시성 프로그래밍',
          'Java 디자인 패턴과 SOLID 원칙',
          '함수형 프로그래밍과 반응형 프로그래밍'
        ]
      },
      {
        title: '2. Spring Framework 기초',
        description: 'Spring Framework의 핵심 개념과 아키텍처를 이해하고 기본 기능을 학습합니다.',
        lessons: [
          'Spring Framework 개요와 핵심 개념',
          '의존성 주입(DI)과 제어의 역전(IoC)',
          'Spring Bean과 컨테이너',
          'AOP(관점 지향 프로그래밍)',
          'Spring MVC 아키텍처',
          'Spring Boot 소개와 환경 설정'
        ]
      },
      {
        title: '3. Spring Boot와 웹 애플리케이션 개발',
        description: 'Spring Boot를 활용한 웹 애플리케이션 개발과 RESTful API 구현 방법을 학습합니다.',
        lessons: [
          'Spring Boot의 Auto-Configuration',
          'RESTful 웹 서비스 개발',
          'Spring Data와 데이터 액세스',
          'Spring Security를 이용한 인증과 권한 관리',
          '예외 처리와 유효성 검사',
          '테스트 주도 개발과 Spring Testing'
        ]
      },
      {
        title: '4. 데이터베이스와 ORM',
        description: '관계형 데이터베이스와 JPA를 활용한 데이터 처리 방법을 학습합니다.',
        lessons: [
          '관계형 데이터베이스 설계 및 최적화',
          'JPA와 Hibernate 기본 개념',
          'Spring Data JPA 활용',
          '엔티티 관계 매핑과 고급 쿼리',
          '트랜잭션 관리와 동시성 제어',
          'NoSQL 데이터베이스 통합 (MongoDB)'
        ]
      },
      {
        title: '5. 마이크로서비스 아키텍처',
        description: '마이크로서비스 기반의 애플리케이션 설계와 개발 방법을 학습합니다.',
        lessons: [
          '마이크로서비스 아키텍처 개요',
          'Spring Cloud를 활용한 마이크로서비스 구현',
          '서비스 디스커버리와 API 게이트웨이',
          '분산 트랜잭션과 일관성 관리',
          '서킷 브레이커와 회복성 패턴',
          '이벤트 기반 아키텍처와 메시징'
        ]
      }
    ]
  };
  
  // 프로젝트 데이터
  const projectsData = {
    subtitle: '실무에서 바로 활용할 수 있는 기술을 적용한 실전형 프로젝트를 진행합니다.',
    projects: projectsByCourse['java-advanced'],
  };
  
  // 강사진 데이터
  const instructorsData = {
    subtitle: '실무 경험이 풍부한 전문가들이 Java 심화 과정을 지도합니다.',
    instructors: [
      {
        name: 'Rechard Park',
        title: '시니어 백엔드 아키텍트',
        bio: '15년 이상의 Java 엔터프라이즈 시스템 개발 경험을 보유한 전문가입니다. 국내 대형 금융 기관의 시스템 아키텍처 설계 및 구현을 담당했으며, Spring 프레임워크와 마이크로서비스 아키텍처에 대한 깊은 지식을 갖추고 있습니다.',
        image: '/assets/images/instructor-park-jiwon.png'
      }
    ]
  };
  
  // CTA 데이터
  const ctaData = {
    title: 'Java 심화 과정으로 실무 역량을 강화하세요',
    subtitle: 'ElectroWave Club의 Java 심화 과정을 통해 실무에서 바로 활용할 수 있는 고급 기술과 프로젝트 경험을 쌓으세요.',
    bgColor: colors.javaAdvancedColor,
    endColor: '#1A237E'  // 진한 인디고 색상
  };
  
  return (
    <>
      <Seo
        title="Java 심화 / Spring"
        description="고급 Java, Spring, 클라우드, 마이크로서비스까지 실무 중심 1:1 심화 멘토링 과정."
        path="/java-advanced"
        ogImage="/assets/images/course-thumb-java-advanced.png"
        ogType="article"
        jsonLd={courseJsonLd}
      />
      <CourseHeader {...headerData} />
      <CourseAbout {...aboutData} />
      <CourseCurriculum {...curriculumData} />
      <CourseProjects {...projectsData} courseSlug="java-advanced" />
      <CourseInstructors {...instructorsData} />
      <CourseReviewSection courseTitle="Java 심화" />
      <CourseCta {...ctaData} />
    </>
  );
};

export default JavaAdvancedPage;
